import { useMemo, useState } from 'react';
import { ChevronDown, RotateCcw } from 'lucide-react';
import { useReference } from '@/hooks/useApp';
import { Input, Switch, ToggleChip } from '@/components/ui';
import { MakeModelFilter } from '@/components/MakeModelFilter';
import { CarSilhouette } from '@/components/CarSilhouette';
import { cn } from '@/lib/cn';
import type { Filters } from '@/hooks/useFilters';
import type { EnumOption, EquipmentOption } from '@/lib/types';

type ArrayKey =
  | 'fuelTypes'
  | 'bodyTypes'
  | 'transmissions'
  | 'driveTypes'
  | 'conditions'
  | 'damageStatuses'
  | 'origins'
  | 'sellerTypes'
  | 'makeIds'
  | 'modelIds'
  | 'cityIds';

interface Props {
  filters: Filters;
  setFilters: (update: Partial<Filters>) => void;
  toggleInArray: (key: ArrayKey, value: number) => void;
  toggleEquipment: (flag: number) => void;
  clear: () => void;
  activeCount: number;
  /** The mobile sheet already has a titled header of its own; two would be one too many. */
  hideHeader?: boolean;
}

/** A collapsible group. Collapsed by default keeps the panel scannable instead of a wall of boxes. */
function Section({
  title,
  children,
  defaultOpen = false,
  badge,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badge?: number;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-default last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="group flex w-full items-center justify-between gap-2 py-3.5 text-left"
      >
        <span className="flex items-center gap-2 text-[13px] font-semibold tracking-wide text-body uppercase">
          {title}
          {badge ? (
            <span className="animate-pop-in flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-accent px-1.5 text-[10px] font-bold text-on-accent">
              {badge}
            </span>
          ) : null}
        </span>
        <ChevronDown
          size={16}
          className={cn(
            'shrink-0 text-faint transition duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:text-body',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && <div className="animate-fade-in-down pb-4">{children}</div>}
    </div>
  );
}

/**
 * Enum values as toggle pills rather than a column of checkboxes.
 *
 * Six fuel types in a wrapped row take a third of the vertical space the checkbox list took, which
 * is what lets the panel show more than two filters at once on a phone.
 */
function PillGroup({
  options,
  selected,
  onToggle,
  icons,
}: {
  options: EnumOption[] | undefined;
  selected: number[];
  onToggle: (value: number) => void;
  /** Body styles get their silhouette; everything else is text only. */
  icons?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options?.map((option, index) => {
        const active = selected.includes(option.value);

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onToggle(option.value)}
            aria-pressed={active}
            style={{ '--i': Math.min(index, 10) } as React.CSSProperties}
            className={cn(
              'animate-fade-in stagger press flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12.5px] font-medium transition duration-300',
              active
                ? 'border-transparent bg-accent text-on-accent shadow-sm'
                : 'border-default bg-raised text-muted hover:border-strong hover:text-body',
            )}
          >
            {icons && <CarSilhouette bodyType={option.value} className="w-4 shrink-0" />}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function Range({
  fromValue,
  toValue,
  onFrom,
  onTo,
  placeholderFrom = 'Od',
  placeholderTo = 'Do',
  suffix,
}: {
  fromValue?: number;
  toValue?: number;
  onFrom: (value?: number) => void;
  onTo: (value?: number) => void;
  placeholderFrom?: string;
  placeholderTo?: string;
  suffix?: string;
}) {
  const parse = (raw: string) => (raw === '' ? undefined : Number(raw));

  return (
    <div className="flex items-center gap-2">
      <Input
        type="number"
        inputMode="numeric"
        placeholder={placeholderFrom}
        value={fromValue ?? ''}
        onChange={(event) => onFrom(parse(event.target.value))}
        className="h-10 tabular-nums"
      />
      <span className="text-faint">–</span>
      <Input
        type="number"
        inputMode="numeric"
        placeholder={placeholderTo}
        value={toValue ?? ''}
        onChange={(event) => onTo(parse(event.target.value))}
        className="h-10 tabular-nums"
      />
      {suffix && <span className="w-6 shrink-0 text-xs text-faint">{suffix}</span>}
    </div>
  );
}

export function FilterPanel({
  filters,
  setFilters,
  toggleInArray,
  toggleEquipment,
  clear,
  activeCount,
  hideHeader = false,
}: Props) {
  const { data: reference } = useReference();

  const equipmentGroups = useMemo(() => {
    const groups = new Map<string, EquipmentOption[]>();

    for (const item of reference?.equipment ?? []) {
      const existing = groups.get(item.group) ?? [];
      existing.push(item);
      groups.set(item.group, existing);
    }

    return [...groups.entries()];
  }, [reference]);

  const equipmentCount = useMemo(() => {
    let count = 0;
    let mask = filters.equipment;

    while (mask) {
      count += mask & 1;
      mask >>>= 1;
    }

    return count;
  }, [filters.equipment]);

  return (
    <div className="flex flex-col">
      {/*
        Sticky rather than fixed: the panel's own scroll container is whatever wraps it — the sticky
        sidebar on desktop, the sheet body on a phone — so the heading stays put without either
        container needing to know it exists.

        It spans the full width of that container, padding included, and sits flush against its top
        edge. Anything narrower leaves a gutter for the scrolling marque tiles to show through,
        which reads as the filters bleeding out of the panel. The negative margin therefore has to
        match the scroll container's horizontal padding.
      */}
      {!hideHeader && (
        <div className="sticky top-0 z-10 -mx-2 flex items-center justify-between gap-2 bg-raised px-2 pt-2.5 pb-2">
          <h2 className="font-display text-sm font-bold tracking-wide text-body uppercase">
            Filteri
          </h2>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={clear}
              className="press animate-fade-in flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-muted transition hover:bg-sunken hover:text-danger"
            >
              <RotateCcw size={12} />
              Poništi ({activeCount})
            </button>
          )}
        </div>
      )}

      <div>
        <Section
          title="Marka i model"
          defaultOpen
          badge={filters.makeIds.length + filters.modelIds.length}
        >
          {/*
            The one filter that had to change. Models now live inside the make that owns them, so
            an Audi A7 and a BMW 7 can never appear as siblings in the same list.
          */}
          <MakeModelFilter
            makeIds={filters.makeIds}
            modelIds={filters.modelIds}
            onChange={(next) => setFilters(next)}
          />
        </Section>

        <Section title="Cena" defaultOpen>
          <Range
            fromValue={filters.priceFrom}
            toValue={filters.priceTo}
            onFrom={(value) => setFilters({ priceFrom: value })}
            onTo={(value) => setFilters({ priceTo: value })}
            suffix="€"
          />
        </Section>

        <Section title="Godište" defaultOpen>
          <Range
            fromValue={filters.yearFrom}
            toValue={filters.yearTo}
            onFrom={(value) => setFilters({ yearFrom: value })}
            onTo={(value) => setFilters({ yearTo: value })}
            placeholderFrom={String(reference?.yearRange.min ?? 1950)}
            placeholderTo={String(reference?.yearRange.max ?? new Date().getFullYear())}
          />
        </Section>

        <Section title="Kilometraža">
          <Range
            fromValue={filters.mileageFrom}
            toValue={filters.mileageTo}
            onFrom={(value) => setFilters({ mileageFrom: value })}
            onTo={(value) => setFilters({ mileageTo: value })}
            suffix="km"
          />
        </Section>

        <Section title="Gorivo" badge={filters.fuelTypes.length} defaultOpen>
          <PillGroup
            options={reference?.enums.fuelTypes}
            selected={filters.fuelTypes}
            onToggle={(value) => toggleInArray('fuelTypes', value)}
          />
        </Section>

        <Section title="Karoserija" badge={filters.bodyTypes.length}>
          <PillGroup
            options={reference?.enums.bodyTypes}
            selected={filters.bodyTypes}
            onToggle={(value) => toggleInArray('bodyTypes', value)}
            icons
          />
        </Section>

        <Section title="Menjač" badge={filters.transmissions.length}>
          <PillGroup
            options={reference?.enums.transmissions}
            selected={filters.transmissions}
            onToggle={(value) => toggleInArray('transmissions', value)}
          />
        </Section>

        <Section title="Pogon" badge={filters.driveTypes.length}>
          <PillGroup
            options={reference?.enums.driveTypes}
            selected={filters.driveTypes}
            onToggle={(value) => toggleInArray('driveTypes', value)}
          />
        </Section>

        <Section title="Snaga motora">
          <Range
            fromValue={filters.powerKwFrom}
            toValue={filters.powerKwTo}
            onFrom={(value) => setFilters({ powerKwFrom: value })}
            onTo={(value) => setFilters({ powerKwTo: value })}
            suffix="kW"
          />
        </Section>

        <Section title="Lokacija" badge={filters.cityIds.length}>
          <CityPicker
            selected={filters.cityIds}
            onToggle={(id) => toggleInArray('cityIds', id)}
          />
        </Section>

        <Section
          title="Stanje i poreklo"
          badge={filters.conditions.length + filters.damageStatuses.length + filters.origins.length}
        >
          <div className="space-y-3">
            <PillGroup
              options={reference?.enums.conditions}
              selected={filters.conditions}
              onToggle={(value) => toggleInArray('conditions', value)}
            />
            <PillGroup
              options={reference?.enums.damageStatuses}
              selected={filters.damageStatuses}
              onToggle={(value) => toggleInArray('damageStatuses', value)}
            />
            <PillGroup
              options={reference?.enums.origins}
              selected={filters.origins}
              onToggle={(value) => toggleInArray('origins', value)}
            />
          </div>
        </Section>

        <Section title="Prodavac" badge={filters.sellerTypes.length}>
          <PillGroup
            options={reference?.enums.sellerTypes}
            selected={filters.sellerTypes}
            onToggle={(value) => toggleInArray('sellerTypes', value)}
          />
        </Section>

        <Section title="Oprema" badge={equipmentCount}>
          <div className="space-y-4">
            {equipmentGroups.map(([group, items]) => (
              <div key={group}>
                <p className="mb-1.5 text-[11px] font-semibold tracking-wide text-faint uppercase">
                  {group}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {items.map((item) => (
                    <ToggleChip
                      key={item.value}
                      label={item.label}
                      active={Boolean(filters.equipment & item.value)}
                      onToggle={() => toggleEquipment(item.value)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Ostalo" defaultOpen>
          <div className="divide-y divide-default">
            <Switch
              label="Samo sa slikama"
              checked={Boolean(filters.withPhotosOnly)}
              onChange={(checked) => setFilters({ withPhotosOnly: checked || undefined })}
            />
            <Switch
              label="Registrovan"
              checked={Boolean(filters.registeredOnly)}
              onChange={(checked) => setFilters({ registeredOnly: checked || undefined })}
            />
            <Switch
              label="Servisna knjiga"
              checked={Boolean(filters.serviceBook)}
              onChange={(checked) => setFilters({ serviceBook: checked || undefined })}
            />
            <Switch
              label="Prvi vlasnik"
              checked={Boolean(filters.firstOwner)}
              onChange={(checked) => setFilters({ firstOwner: checked || undefined })}
            />
            <Switch
              label="Moguća zamena"
              checked={Boolean(filters.exchangeAccepted)}
              onChange={(checked) => setFilters({ exchangeAccepted: checked || undefined })}
            />
          </div>
        </Section>
      </div>
    </div>
  );
}

/** Cities are grouped by region, because nobody in Niš scrolls an alphabetical list to find it. */
function CityPicker({
  selected,
  onToggle,
}: {
  selected: number[];
  onToggle: (id: number) => void;
}) {
  const { data: reference } = useReference();
  const [term, setTerm] = useState('');

  const query = term.trim().toLowerCase();

  const visible = useMemo(() => {
    const cities = reference?.cities ?? [];

    if (query) {
      return cities.filter((city) => city.name.toLowerCase().includes(query));
    }

    // Without a search term the list is capped: 200 city pills is not a filter either.
    return cities.slice(0, 24);
  }, [reference, query]);

  return (
    <div className="space-y-2.5">
      <Input
        type="search"
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        placeholder="Pronađi grad…"
        aria-label="Pretraga gradova"
        className="h-10 text-sm"
      />

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((id) => {
            const city = reference?.cities.find((item) => item.id === id);

            return (
              <button
                key={id}
                type="button"
                onClick={() => onToggle(id)}
                className="press animate-pop-in rounded-full border border-transparent bg-accent px-3 py-1.5 text-[12.5px] font-medium text-on-accent shadow-sm"
              >
                {city?.name ?? id} ×
              </button>
            );
          })}
        </div>
      )}

      <div className="flex max-h-48 flex-wrap gap-1.5 overflow-y-auto overscroll-contain">
        {visible
          .filter((city) => !selected.includes(city.id))
          .map((city) => (
            <button
              key={city.id}
              type="button"
              onClick={() => onToggle(city.id)}
              className="press rounded-full border border-default bg-raised px-3 py-1.5 text-[12.5px] font-medium text-muted transition hover:border-strong hover:text-body"
            >
              {city.name}
            </button>
          ))}
      </div>
    </div>
  );
}
